import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import clientApi from "@/lib/clientApi";
import { useLang } from "@/context/LanguageContext";

export default function ClientResetPasswordPage() {
  const { token } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error(t("portal.passwordsNoMatch"));
      return;
    }
    setSubmitting(true);
    try {
      await clientApi.post("/client/reset-password", { token, new_password: form.new_password });
      setDone(true);
      setTimeout(() => navigate("/portal/login"), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.resetLinkInvalid"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-gold grid place-items-center px-4">
      <div className="w-full max-w-md card-dark p-8 sm:p-10 gold-glow text-center" data-testid="client-reset-password-card">
        {done ? (
          <>
            <div className="w-14 h-14 mx-auto mb-4 grid place-items-center rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/30">
              <Check size={22} className="text-[var(--gold)]" />
            </div>
            <h1 className="font-display text-xl font-bold text-white mb-2">{t("portal.passwordResetDone")}</h1>
            <p className="text-white/60 text-sm">{t("portal.redirecting")}</p>
          </>
        ) : (
          <>
            <h1 className="font-display text-xl font-bold text-white mb-2">{t("portal.setNewPassword")}</h1>
            <form onSubmit={submit} className="space-y-4 text-left mt-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.newPassword")}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
                  <input
                    required
                    minLength={6}
                    type="password"
                    value={form.new_password}
                    onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                    className="input-dark pl-10"
                    data-testid="reset-password-new"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.confirmPassword")}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
                  <input
                    required
                    minLength={6}
                    type="password"
                    value={form.confirm_password}
                    onChange={(e) => setForm((f) => ({ ...f, confirm_password: e.target.value }))}
                    className="input-dark pl-10"
                    data-testid="reset-password-confirm"
                  />
                </div>
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="reset-password-submit">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {t("portal.resetPasswordButton")}
              </button>
            </form>
          </>
        )}
        <Link to="/portal/login" className="inline-block mt-6 text-xs text-white/50 hover:text-[var(--gold)] transition">
          {t("portal.backToSignIn")}
        </Link>
      </div>
    </div>
  );
}
